import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { connect } from 'react-redux';
import { openMainModal } from '../../store/actions';
import { 
  validateOAuthState, 
  cleanupOAuthData, 
  getProjectDataFromOAuth,
  getCodeVerifierFromOAuth
} from '../../utils/mercadoPagoOAuth.ts';
import { mercadoPagoOAuthService } from '../../services/mercadoPagoOAuthService.ts';
import CreationProgressModal from '../../components/UI/CreationProgressModal/CreationProgressModal.tsx';
import { buildProfileUrl } from '../../utils/navigationHelpers.ts';
import environment from '../../environments/index';
import { Loader2, AlertCircle } from 'lucide-react';
import styles from './OAuthCallback.module.scss';

interface RootState {
  general: {
    userData: {
      id: string;
      username: string;
      name?: string;
      last_name?: string;
      razon_social?: string;
      type: string | null;
    } | null;
    isAuth: boolean;
  };
}

interface OAuthCallbackProps {
  openMainModal: (config: any) => void;
}

const OAuthCallback: React.FC<OAuthCallbackProps> = ({ openMainModal }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [redirectToEditOnSuccess, setRedirectToEditOnSuccess] = useState(false);
  const [modalState, setModalState] = useState<'creating' | 'uploading' | 'success' | 'pending' | 'error' | null>(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  const userData = useSelector((state: RootState) => state.general.userData);
  const userId = userData?.id;
  const isAuth = useSelector((state: RootState) => state.general.isAuth);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Validar autenticación
        if (!isAuth || !userId) {
          throw new Error('Usuario no autenticado');
        }

        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!state || !validateOAuthState(state)) {
          throw new Error('Estado de seguridad inválido');
        }
        if (!code) {
          throw new Error('Código de autorización no recibido');
        }
        
        const projectData = await getProjectDataFromOAuth();
        if (!projectData) {
          throw new Error('Datos del proyecto no encontrados');
        }

        const codeVerifier = getCodeVerifierFromOAuth();
        if (!codeVerifier) {
          throw new Error('Code verifier no encontrado');
        }

        await mercadoPagoOAuthService.exchangeCodeForToken(code, codeVerifier);

        setLoading(false);
        setModalState('creating');

        const { formData, fileItems, selectedType, selectedSubCategory, selectedMaterialities, selectedUses } = projectData;
        const additionalData = {
          selectedType,
          selectedSubCategory,
          selectedMaterialities,
          selectedUses
        };
        
        const handleUploadProgress = (current: number, total: number) => {
          setModalState('uploading');
          setUploadProgress({ current, total });
        };
        
        const result = await mercadoPagoOAuthService.createProjectDocumentation(
          formData, 
          fileItems, 
          additionalData, 
          userId,
          handleUploadProgress
        );
        
        const createdProjectId = result?.data?.id;
        const failures = result?.failures || [];
        if (createdProjectId) {
          setProjectId(createdProjectId);
          if (failures.length > 0) {
            setRedirectToEditOnSuccess(true);
          }
          await cleanupOAuthData();
          setModalState('pending');
        } else {
          throw new Error('No se pudo obtener el ID del proyecto creado');
        }
        
      } catch (err) {
        console.error('Error OAuth:', err);
        setError(err instanceof Error ? err.message : 'Error desconocido');
        setLoading(false);
        setModalState('error');
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, openMainModal, userId, isAuth]);

  const handleModalSuccess = () => {
    setModalState(null);
    if (redirectToEditOnSuccess && projectId) {
      navigate(`/documentacion-de-obra/edit/${projectId}`, { state: { uploadFailures: true } });
    } else if (userData) {
      const userName = userData.type === "COMPANY" 
        ? userData.razon_social 
        : `${userData.name} ${userData.last_name}`;
      const profileUrl = buildProfileUrl(String(userData.id), userName, 'Documentaciones de obra');
      navigate(profileUrl);
    } else {
      navigate('/dashboard');
    }
  };

  const handleModalError = () => {
    setModalState(null);
    navigate('/documentacion-de-obra/crear');
  };

  const handleViewPreview = () => {
    if (projectId) {
      const marketplaceUrl = environment.marketplace.url;
      const previewUrl = `${marketplaceUrl}/proyecto/${projectId}`;
      window.open(previewUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <Loader2 size={48} className={styles.loadingIcon} />
          </div>
          <h2 className={styles.title}>
            Procesando autorización...
          </h2>
          <p className={styles.bodyText}>
            Por favor espera mientras completamos la vinculación con tu cuenta de Mercado Pago y creamos tu proyecto.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {modalState && (
        <CreationProgressModal
          isOpen={modalState !== null}
          state={modalState}
          message={
            modalState === 'creating' 
              ? 'Creando proyecto...'
              : modalState === 'uploading'
              ? 'Subiendo archivos...'
              : modalState === 'pending'
              ? 'Tu proyecto ha sido creado exitosamente y está pendiente de aprobación por nuestro equipo. Una vez aprobado, será visible públicamente en el marketplace.'
              : `Error: ${error}`
          }
          progress={modalState === 'uploading' ? uploadProgress : undefined}
          projectId={projectId ? String(projectId) : undefined}
          projectPreviewUrl={projectId ? `${environment.marketplace.url}/proyecto/${projectId}` : undefined}
          onSuccess={handleModalSuccess}
          onError={handleModalError}
          onViewPreview={handleViewPreview}
        />
      )}
      {error && modalState !== 'error' && (
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.iconContainer}>
              <AlertCircle size={48} className={styles.errorIcon} />
            </div>
            <div className={styles.errorContainer}>
              <h2 className={styles.errorTitle}>
                Error en la autorización
              </h2>
              <p className={styles.errorMessage}>
                {error}
              </p>
            </div>
            <button 
              onClick={() => navigate('/documentacion-de-obra/crear')}
              className={styles.button}
            >
              Volver al formulario
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default connect(null, {
  openMainModal
})(OAuthCallback);
